/**
 * One approver row of the approval rules (masterplan credit-limits-v3, B5; E4, E26): the salesperson, the BT manager
 * and the top level of one sales manager, picked from the active employees. A new row takes its sales manager from the
 * names of the limit list that have no row yet. Rows are never deleted: «Active» switches one off. The API checks
 * everything again; a refusal is shown in the dialog.
 */
import { useId, useState } from 'react';
import type { ApproverUpdateBody, CreditLimitApprover, CreditLimitConfig } from '../../../types/creditLimits';
import { OverviewDialog } from '../overview/OverviewDialog';
import { apiErrorText } from '../requests/requestRules';
import {
    approverBody, approverDraftOf, checkApproverDraft, freeSalesManagers, personOptions, type ApproverDraft,
} from './ruleForm';

interface Props {
    config: CreditLimitConfig;
    /** The row to change; null creates a new one. */
    row: CreditLimitApprover | null;
    onSave: (body: ApproverUpdateBody) => Promise<void>;
    onClose: () => void;
}

const PERSON_FIELDS = [
    { key: 'smEmail', label: 'Salesperson' },
    { key: 'btmEmail', label: 'BT manager' },
    { key: 'topEmail', label: 'Top level' },
] as const;

export function ApproverDialog({ config, row, onSave, onClose }: Props) {
    const [draft, setDraft] = useState<ApproverDraft>(() => approverDraftOf(row, config.settings.defaultTopEmail));
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const uid = useId();
    const errors = checkApproverDraft(draft);
    const free = freeSalesManagers(config.salesManagerOptions, config.approvers);
    const body = approverBody(row, draft);

    const set = <K extends keyof ApproverDraft>(key: K, value: ApproverDraft[K]) => setDraft(d => ({ ...d, [key]: value }));

    const save = async () => {
        setTouched(true);
        if (Object.keys(errors).length > 0 || !body) return;
        setBusy(true);
        setError(null);
        try {
            await onSave(body);
            onClose();
        } catch (err) {
            setError(apiErrorText(err, 'The row could not be saved.'));
            setBusy(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="btn secondary sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn primary sm" onClick={save} disabled={busy || (Object.keys(errors).length === 0 && !body)}>
                {busy ? 'Saving…' : 'Save'}
            </button>
        </>
    );

    return (
        <OverviewDialog
            title={row ? `Approvers: ${row.salesManagerName}` : 'New approver row'}
            subtitle="Open requests keep the approvers they were created with; a change applies to new requests."
            onClose={onClose}
            footer={footer}
            width={640}
        >
            <div style={{ display: 'grid', gap: 12 }}>
                {error && <div className="banner error" role="alert"><p>{error}</p></div>}

                {row ? (
                    <p style={{ margin: 0 }}><b>{row.salesManagerName}</b></p>
                ) : (
                    <div style={{ display: 'grid', gap: 4 }}>
                        <label htmlFor={`${uid}-name`} className="muted">Sales manager (names of the limit list without a row)</label>
                        <select
                            id={`${uid}-name`}
                            className="input"
                            value={draft.salesManagerName}
                            aria-invalid={touched && !!errors.salesManagerName}
                            onChange={e => set('salesManagerName', e.target.value)}
                        >
                            <option value="">Choose…</option>
                            {free.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        {touched && errors.salesManagerName && <span className="hint" role="alert">{errors.salesManagerName}</span>}
                    </div>
                )}

                {PERSON_FIELDS.map(f => (
                    <div key={f.key} style={{ display: 'grid', gap: 4 }}>
                        <label htmlFor={`${uid}-${f.key}`} className="muted">{f.label}</label>
                        <select
                            id={`${uid}-${f.key}`}
                            className="input"
                            value={draft[f.key]}
                            aria-invalid={touched && !!errors[f.key]}
                            onChange={e => set(f.key, e.target.value)}
                        >
                            <option value="">Choose…</option>
                            {personOptions(config.people, config.names, row ? (row[f.key] ?? null) : draft[f.key]).map(o => (
                                <option key={o.email} value={o.email}>{o.label}</option>
                            ))}
                        </select>
                        {touched && errors[f.key] && <span className="hint" role="alert">{errors[f.key]}</span>}
                    </div>
                ))}

                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" className="check" checked={draft.isActive} onChange={e => set('isActive', e.target.checked)} />
                    <span>Active (a switched-off row sends the customer to Finance)</span>
                </label>
            </div>
        </OverviewDialog>
    );
}
